import React, { useState, useEffect, useCallback } from 'react';
import { Card, Typography, Row, Col, Statistic, Spin, DatePicker, Button, Divider, Tag, Modal, Table, List } from 'antd';
import { BarChartOutlined, SoundOutlined, CheckCircleOutlined, DollarOutlined, TeamOutlined, FileProtectOutlined, PictureOutlined, PrinterOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';

const { Title, Text } = Typography;

export default function MonthlyReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(dayjs());
  const [detailModal, setDetailModal] = useState({ open: false, type: '', title: '', data: [], loading: false });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/performance/monthly-report', { params: { month: month.format('YYYY-MM') } });
      setData(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const showDetail = async (type, title) => {
    setDetailModal({ open: true, type, title, data: [], loading: true });
    try {
      const res = await api.get('/performance/monthly-detail', { params: { month: month.format('YYYY-MM'), type } });
      setDetailModal(prev => ({ ...prev, data: res.data?.data || [], loading: false }));
    } catch (err) {
      setDetailModal(prev => ({ ...prev, loading: false }));
    }
  };

  const getDetailColumns = (type) => {
    if (['songs_added', 'total_songs', 'total_distributed', 'total_pending'].includes(type)) {
      return [
        { title: '#', width: 50, render: (_, __, i) => i + 1 },
        { title: 'Title', dataIndex: 'title', ellipsis: true, render: v => <Text strong>{v}</Text> },
        { title: 'Singer', dataIndex: 'singer', width: 150 },
        { title: 'Studio', dataIndex: 'studio', width: 150 },
        { title: 'Status', dataIndex: 'distribution_status', width: 120, render: v => v === 'distributed' ? <Tag color="green">Distributed</Tag> : <Tag color="orange">Pending</Tag> },
        { title: 'Date', dataIndex: 'created_at', width: 140, render: v => dayjs(v).format('DD MMM YYYY') }
      ];
    }
    if (['songs_distributed', 'agreements', 'thumbnails'].includes(type)) {
      return [
        { title: '#', width: 50, render: (_, __, i) => i + 1 },
        { title: 'Staff', dataIndex: 'staff_name', width: 150, render: v => <Text strong>{v || '-'}</Text> },
        { title: 'Details', dataIndex: 'details', ellipsis: true },
        { title: 'Date', dataIndex: 'created_at', width: 160, render: v => dayjs(v).format('DD MMM, hh:mm A') }
      ];
    }
    if (type === 'income' || type === 'expenses') {
      return [
        { title: '#', width: 50, render: (_, __, i) => i + 1 },
        { title: 'Amount', dataIndex: 'amount', width: 120, render: v => <Text strong style={{ color: type === 'income' ? '#52c41a' : '#ff4d4f' }}>₹{Number(v).toLocaleString()}</Text> },
        { title: 'Category', dataIndex: 'category', width: 140, render: v => <Tag>{v}</Tag> },
        { title: 'Description', dataIndex: 'description', ellipsis: true },
        { title: 'Date', dataIndex: 'date', width: 120, render: v => dayjs(v).format('DD MMM YYYY') }
      ];
    }
    return [{ title: 'Data', dataIndex: 'details' }];
  };

  const statCard = (title, value, icon, color, type) => (
    <Col xs={12} sm={6}>
      <Card hoverable style={{ cursor: 'pointer', borderLeft: `3px solid ${color}` }} onClick={() => showDetail(type, title)}>
        <Statistic title={<span>{title} <EyeOutlined style={{ fontSize: 11, color: '#999' }} /></span>} value={value} prefix={icon} valueStyle={{ color }} />
      </Card>
    </Col>
  );

  const handlePrint = () => {
    const printContent = document.getElementById('monthly-report-content');
    if (!printContent) return;
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Monthly Report - ${month.format('MMMM YYYY')}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 30px; color: #333; }
        .header { background: linear-gradient(135deg, #667eea, #764ba2); color: white; padding: 20px 30px; border-radius: 12px; margin-bottom: 20px; }
        .header h1 { margin: 0; font-size: 24px; } .header p { margin: 5px 0 0; opacity: 0.9; }
        .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 20px; }
        .stat-card { border: 1px solid #e8e8e8; border-radius: 8px; padding: 16px; text-align: center; }
        .stat-value { font-size: 28px; font-weight: bold; } .stat-label { font-size: 12px; color: #666; margin-top: 4px; }
        .green { color: #52c41a; } .blue { color: #1890ff; } .orange { color: #faad14; } .red { color: #ff4d4f; } .purple { color: #722ed1; }
        .section { margin: 20px 0; } .section h3 { border-bottom: 2px solid #667eea; padding-bottom: 8px; }
        .footer { text-align: center; color: #999; font-size: 12px; margin-top: 30px; border-top: 1px solid #eee; padding-top: 10px; }
        @media print { body { padding: 10px; } }
      </style></head><body>
      <div class="header"><h1>Monthly Progress Report</h1><p>Bainsla Music Private Limited | ${month.format('MMMM YYYY')}</p></div>
      <div class="stats-grid">
        <div class="stat-card"><div class="stat-value blue">${data?.songs_added || 0}</div><div class="stat-label">Songs Added</div></div>
        <div class="stat-card"><div class="stat-value green">${data?.songs_distributed || 0}</div><div class="stat-label">Songs Distributed</div></div>
        <div class="stat-card"><div class="stat-value purple">${data?.agreements_added || 0}</div><div class="stat-label">Agreements</div></div>
        <div class="stat-card"><div class="stat-value orange">${data?.thumbnails_uploaded || 0}</div><div class="stat-label">Thumbnails</div></div>
      </div>
      <div class="section"><h3>Financial Summary</h3><div class="stats-grid">
        <div class="stat-card"><div class="stat-value green">₹${(data?.income || 0).toLocaleString()}</div><div class="stat-label">Income</div></div>
        <div class="stat-card"><div class="stat-value red">₹${(data?.expenses || 0).toLocaleString()}</div><div class="stat-label">Expenses</div></div>
        <div class="stat-card"><div class="stat-value ${(data?.profit || 0) >= 0 ? 'green' : 'red'}">₹${(data?.profit || 0).toLocaleString()}</div><div class="stat-label">Profit</div></div>
        <div class="stat-card"><div class="stat-value blue">${data?.total_songs || 0}</div><div class="stat-label">Total Songs</div></div>
      </div></div>
      <div class="section"><h3>Overall Status</h3><div class="stats-grid">
        <div class="stat-card"><div class="stat-value blue">${data?.total_songs || 0}</div><div class="stat-label">Total Songs</div></div>
        <div class="stat-card"><div class="stat-value green">${data?.total_distributed || 0}</div><div class="stat-label">Total Distributed</div></div>
        <div class="stat-card"><div class="stat-value orange">${data?.total_pending || 0}</div><div class="stat-label">Total Pending</div></div>
        <div class="stat-card"><div class="stat-value blue">${data?.total_songs > 0 ? Math.round((data.total_distributed / data.total_songs) * 100) : 0}%</div><div class="stat-label">Distribution Rate</div></div>
      </div></div>
      <div class="section"><h3>Staff Attendance Summary</h3><div class="stats-grid">
        <div class="stat-card"><div class="stat-value blue">${data?.attendance?.total_staff || 0}</div><div class="stat-label">Total Staff</div></div>
        <div class="stat-card"><div class="stat-value green">${data?.attendance?.total_present_days || 0}</div><div class="stat-label">Present Days</div></div>
        <div class="stat-card"><div class="stat-value red">${data?.attendance?.total_absent_days || 0}</div><div class="stat-label">Absent Days</div></div>
        <div class="stat-card"><div class="stat-value orange">${data?.attendance?.total_leave_days || 0}</div><div class="stat-label">Leave Days</div></div>
      </div></div>
      <div class="footer">Bainsla Music Data Dashboard | data.intubemedia.com | Generated on ${dayjs().format('DD MMM YYYY, hh:mm A')}</div>
      </body></html>`);
    w.document.close();
    setTimeout(() => { w.print(); }, 500);
  };

  return (
    <div>
      <Row justify="space-between" align="middle">
        <Col><Title level={3}><BarChartOutlined /> Monthly Progress Report</Title></Col>
        <Col>
          <Row gutter={8}>
            <Col><DatePicker picker="month" value={month} onChange={v => setMonth(v || dayjs())} /></Col>
            <Col><Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>Download PDF</Button></Col>
          </Row>
        </Col>
      </Row>
      <Text type="secondary">Click any card to see detailed data. Auto-refreshes every 30 seconds.</Text>

      {loading ? <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: 40 }} /> : data ? (
        <div id="monthly-report-content">
          <Title level={5} style={{ marginTop: 16 }}>This Month's Activity</Title>
          <Row gutter={16}>
            {statCard('Songs Added', data.songs_added, <SoundOutlined />, '#1890ff', 'songs_added')}
            {statCard('Songs Distributed', data.songs_distributed, <CheckCircleOutlined />, '#52c41a', 'songs_distributed')}
            {statCard('Agreements', data.agreements_added, <FileProtectOutlined />, '#722ed1', 'agreements')}
            {statCard('Thumbnails', data.thumbnails_uploaded, <PictureOutlined />, '#faad14', 'thumbnails')}
          </Row>

          <Divider />
          <Title level={5}>Financial Summary</Title>
          <Row gutter={16}>
            <Col xs={8}>
              <Card hoverable style={{ background: '#f6ffed', border: '1px solid #b7eb8f', cursor: 'pointer' }} onClick={() => showDetail('income', 'Income Details')}>
                <Statistic title={<span>Income <EyeOutlined style={{ fontSize: 11, color: '#999' }} /></span>} value={data.income} prefix="₹" valueStyle={{ color: '#52c41a' }} />
              </Card>
            </Col>
            <Col xs={8}>
              <Card hoverable style={{ background: '#fff2f0', border: '1px solid #ffccc7', cursor: 'pointer' }} onClick={() => showDetail('expenses', 'Expenses Details')}>
                <Statistic title={<span>Expenses <EyeOutlined style={{ fontSize: 11, color: '#999' }} /></span>} value={data.expenses} prefix="₹" valueStyle={{ color: '#ff4d4f' }} />
              </Card>
            </Col>
            <Col xs={8}>
              <Card style={{ background: data.profit >= 0 ? '#f6ffed' : '#fff2f0', border: `1px solid ${data.profit >= 0 ? '#b7eb8f' : '#ffccc7'}` }}>
                <Statistic title="Profit" value={data.profit} prefix="₹" valueStyle={{ color: data.profit >= 0 ? '#52c41a' : '#ff4d4f' }} />
              </Card>
            </Col>
          </Row>

          <Divider />
          <Title level={5}>Overall Song Status</Title>
          <Row gutter={16}>
            {statCard('Total Songs', data.total_songs, <SoundOutlined />, '#1890ff', 'total_songs')}
            {statCard('Distributed', data.total_distributed, <CheckCircleOutlined />, '#52c41a', 'total_distributed')}
            {statCard('Pending', data.total_pending, null, '#faad14', 'total_pending')}
            <Col xs={12} sm={6}>
              <Card><Statistic title="Distribution Rate" value={data.total_songs > 0 ? Math.round((data.total_distributed / data.total_songs) * 100) : 0} suffix="%" valueStyle={{ color: '#667eea' }} /></Card>
            </Col>
          </Row>

          <Divider />
          <Title level={5}>Staff Attendance Summary</Title>
          <Row gutter={16}>
            <Col xs={6}><Card size="small"><Statistic title="Staff" value={data.attendance?.total_staff || 0} prefix={<TeamOutlined />} /></Card></Col>
            <Col xs={6}><Card size="small"><Statistic title="Present Days" value={data.attendance?.total_present_days || 0} valueStyle={{ color: '#52c41a' }} /></Card></Col>
            <Col xs={6}><Card size="small"><Statistic title="Absent Days" value={data.attendance?.total_absent_days || 0} valueStyle={{ color: '#ff4d4f' }} /></Card></Col>
            <Col xs={6}><Card size="small"><Statistic title="Leave Days" value={data.attendance?.total_leave_days || 0} valueStyle={{ color: '#faad14' }} /></Card></Col>
          </Row>
        </div>
      ) : null}

      <Modal
        title={detailModal.title}
        open={detailModal.open}
        onCancel={() => setDetailModal(prev => ({ ...prev, open: false }))}
        footer={null}
        width={900}
      >
        {detailModal.loading ? <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: 40 }} /> : (
          <Table
            columns={getDetailColumns(detailModal.type)}
            dataSource={detailModal.data}
            rowKey={(r, i) => r.id || i}
            size="small"
            pagination={{ pageSize: 20 }}
            scroll={{ x: 700 }}
          />
        )}
      </Modal>
    </div>
  );
}
